import { Engine } from '@/core/engine';
import { Input } from '@/input/input';
import { RNG, hashSeed } from '@/utils/rng';
import { City } from '@/world/city';
import { CityGenerator } from '@/generation/city-generator';
import { Ground } from '@/rendering/ground';
import { Lighting } from '@/rendering/lighting';
import { BuildingField } from '@/rendering/building-field';
import { SkyLinkField } from '@/rendering/skylink-field';
import { OrnamentField } from '@/rendering/ornament-field';
import { Sky } from '@/rendering/sky';
import { Mountains } from '@/rendering/mountains';
import { Motes } from '@/rendering/motes';
import { PlayerController } from '@/player/player-controller';
import { PlayerAnalyzer } from '@/ai/player-analyzer';
import { CityEvolutionEngine } from '@/ai/city-evolution-engine';
import { EnvironmentController } from '@/ai/environment-controller';
import { NarrativeController } from '@/ai/narrative-controller';
import { WorldRenderSystem } from '@/world/world-render-system';
import { UISystem } from '@/ui/ui-system';
import { AudioSystem } from '@/audio/audio-system';
import { SaveSystem } from '@/systems/save-system';
import { PathRecorder } from '@/systems/path-recorder';
import { FinaleDirector } from '@/systems/finale-director';
import { getStage } from '@/utils/viewport';

/**
 * Composition root.
 *
 * This is the only file that knows about every subsystem at once. It wires them
 * together in the correct order and hands each the collaborators it needs — a
 * manual dependency-injection that keeps every other module ignorant of the whole.
 *
 * Update order is deliberate: the player moves and emits telemetry; the analyzer
 * (listening) updates its read of the player; the evolution engine reshapes the
 * city; the environment recolours the mood; narrative watches the maturity; and the
 * world-render system reconciles the GPU with the freshly-mutated city, last, so no
 * change waits a frame to appear.
 */
function pickSeed(): { seed: number; reset: boolean } {
  const params = new URLSearchParams(location.search);
  if (params.has('reset')) SaveSystem.clear();
  const raw = params.get('seed');
  if (raw) {
    const n = Number(raw);
    return { seed: Number.isFinite(n) && raw.trim() !== '' ? n >>> 0 : hashSeed(raw), reset: params.has('reset') };
  }
  // Adopt the seed of an existing save so the player returns to *their* city.
  try {
    const saved = localStorage.getItem('echo-city-save');
    if (saved && !params.has('reset')) {
      const seed = JSON.parse(saved).seed;
      if (typeof seed === 'number') return { seed: seed >>> 0, reset: false };
    }
  } catch {
    /* ignore */
  }
  // Fresh session: derive a session seed. (Not part of procedural generation, so
  // using the wall clock here is fine — it merely selects which city you'll grow.)
  return { seed: (Date.now() & 0x7fffffff) >>> 0, reset: params.has('reset') };
}

function boot(): void {
  const container = document.getElementById('app');
  if (!container) throw new Error('#app container missing');

  // Tag the rotation stage BEFORE the engine sizes its canvas: on a touch device in
  // portrait this flips #stage to landscape via CSS, so the engine reads landscape
  // dimensions from the very first frame instead of a rotated portrait buffer.
  getStage();

  const { seed } = pickSeed();
  const engine = new Engine(container);

  // --- Logical world -------------------------------------------------
  const rng = new RNG(seed);
  const city = new City(rng);
  const generator = new CityGenerator(city);

  // --- Rendering fields (created before the AI so the finale can ripple) ---
  const ground = new Ground(engine.scene, city);
  const lighting = new Lighting(engine.scene);
  const buildings = new BuildingField(engine.scene);
  const links = new SkyLinkField(engine.scene);
  const ornaments = new OrnamentField(engine.scene);

  // --- The shan-shui atmosphere layer --------------------------------
  const sky = new Sky(engine.scene);
  const mountains = new Mountains(engine.scene, rng.fork(4242));
  const motes = new Motes(engine.scene);

  // --- AI / persistence collaborators ---------------------------------
  const analyzer = new PlayerAnalyzer(engine);
  const narrative = new NarrativeController(engine, city, ground);
  const recorder = new PathRecorder(engine);
  const save = new SaveSystem(seed, city, analyzer.memory, narrative, recorder);

  const restored = save.load();
  if (!restored) generator.generateInitial();
  ground.syncFromCity(city);

  // --- Player --------------------------------------------------------
  const input = new Input(engine.renderer.domElement, engine.events);
  const player = new PlayerController(engine, input, city);
  const playerPos = () => player.position;

  // --- Systems (registration order == update order) ------------------
  engine.add(player);
  engine.add(analyzer);
  engine.add(new CityEvolutionEngine(engine, city, generator, ground, buildings, links, analyzer, playerPos));
  const environment = new EnvironmentController(engine, city, lighting, buildings, playerPos);
  environment.attachAtmosphere({ sky, mountains, motes });
  engine.add(environment);
  engine.add(narrative);
  engine.add(recorder);
  // The finale director runs before the world-render reconciliation so its
  // memory-wave writes land in the same frame they are computed.
  engine.add(new FinaleDirector(engine, city, ground, buildings, links, ornaments, player, recorder));
  engine.add(new AudioSystem(engine, city));
  engine.add(save);
  engine.add(new WorldRenderSystem(city, buildings, links, ground, ornaments));
  engine.add(new UISystem(engine, city, analyzer));

  engine.start();

  // Belt-and-suspenders: force one resize now that every overlay is mounted and the
  // stage is tagged, so the renderer matches the (possibly rotated) landscape stage.
  window.dispatchEvent(new Event('resize'));

  // Persist on tab close.
  window.addEventListener('beforeunload', () => save.save());

  // Expose for debugging in the console.
  (window as unknown as { echo?: unknown }).echo = { engine, city, analyzer };
}

/**
 * Register the service worker so Palimpsest is installable (Add to Home Screen)
 * and launches offline. Production only — a SW in dev would fight Vite's HMR.
 */
function registerServiceWorker(): void {
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        /* install path is best-effort; the app runs fine without it */
      });
    });
  }
}

boot();
registerServiceWorker();
